import { Navigate } from 'react-router-dom';

import BrandLoadingScreen from '@/components/shared/BrandLoadingScreen';
import { useIsSudo } from '@/hooks/useIsSudo';
import { useAuth } from '@/contexts/AuthContext';

/**
 * SudoGuard — wraps every `/admin/*` route so only Firebase users with
 * the `sudo: true` custom claim can render the Backoffice.
 *
 * States:
 *   - Auth still bootstrapping → shows a centered spinner (the same
 *     pattern used by `ProtectedRoute` in App.tsx).
 *   - Authenticated but no sudo claim → silent redirect to `/dashboard`.
 *     We deliberately don't 401 — that would expose the existence of
 *     the admin area to non-admins. Recruiters who fat-fingered an
 *     admin URL just bounce back to their normal home.
 *   - Authenticated + sudo → render children.
 *
 * Note: a freshly granted sudo claim only takes effect after the
 * Firebase ID token is refreshed. `useIsSudo()` calls
 * `getIdTokenResult(true)` to force refresh on first load, so a
 * recently sudo-granted admin can reach the panel without a manual
 * sign-out / sign-in roundtrip.
 */
export function SudoGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isSudo, isLoading: sudoLoading } = useIsSudo();

  if (authLoading || sudoLoading) {
    return <BrandLoadingScreen />;
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSudo) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
